import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import anime from "animejs";
import {
  setShowFreeSpinIntro,
  setFreeSpinTransitionActive,
} from "../../../../State/actions";
import { Settings } from "../../../../settings";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { SOUND_NAME } from "../../../../sounds";
// Temporarily disabled — spine centered at screen center
// import { GAME_CONTAINER_OFFSET_CONFIG } from "../../../../Games/Main/GameContainer/GameContainer";

// Device and orientation-specific size configuration for the free spin intro dialog
const INTRO_SIZE_CONFIG = {
  MOBILE: {
    PORTRAIT: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.85,
      buttonFontScale: 1.3,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.05,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 0.75,
      buttonFontScale: 1.2,
    },
  },
  TABLET: {
    PORTRAIT: {
      spineScaleMultiplier: 1.2,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 1.35,
    },
    LANDSCAPE: {
      spineScaleMultiplier: 1.0,
      spineYOffset: 0,
      buttonYOffsetMultiplier: 1.5,
      buttonFontScale: 1.35,
    },
  },
  DESKTOP: {
    spineScaleMultiplier: 1.0,
    spineYOffset: 0,
    buttonYOffsetMultiplier: 1.0,
    buttonFontScale: 1.0,
  },
};

/**
 * FreeSpinIntroDialogController - Handles free spin intro animation sequence
 * Requires manual user interaction to close (button click or overlay click)
 * Modeled on HoldAndWinIntroController
 */
class FreeSpinIntroDialogController {
  constructor(
    container,
    dimensionsContext,
    dispatch,
    symbolHeight,
    overlayRef,
    buttonRef,
    t,
  ) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;
    this.overlayRef = overlayRef;
    this.buttonRef = buttonRef;
    this.t = t;

    // Extract dimensions object for backward compatibility
    this.dimensions = dimensionsContext.dimensions;

    // Spine instance
    this.introSpine = null;
    this.isPlaying = false;
    this.initialized = false;
    this.isWaitingForClick = false;

    // User interaction state (matching H&W intro pattern)
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;
    this.debounceTimeout = null;
    this.skipProtectionTimeout = null;
    this.currentTimeout = null;

    // Button attached to spine slot
    this.buttonContainer = null;
    this.buttonShadowText = null;
    this.buttonText = null;

    // Internal caches for performance optimization
    this._configCache = null;
    this._deviceScaleMultiplierCache = null;
    this._availableAnimationsCache = null;
    this._lastDevice = null;
    this._lastOrientation = null;

    this.initializeIntroSpine();
  }

  /**
   * Get configuration based on current device and orientation
   */
  getConfig() {
    const { device, orientation } = this.dimensionsContext;

    // Return cached result if device/orientation unchanged
    if (
      this._configCache !== null &&
      this._lastDevice === device &&
      this._lastOrientation === orientation
    ) {
      return this._configCache;
    }

    // Map device string to config key
    let deviceKey;
    if (device === PixiSettings.DEVICE.MOBILE) {
      deviceKey = "MOBILE";
    } else if (device === PixiSettings.DEVICE.TABLET) {
      deviceKey = "TABLET";
    } else {
      deviceKey = "DESKTOP";
    }

    // Map orientation string to config key
    const orientationKey =
      orientation === PixiSettings.ORIENTATION.PORTRAIT
        ? "PORTRAIT"
        : "LANDSCAPE";

    // Get device-specific config, fallback to DESKTOP LANDSCAPE
    const deviceConfig =
      INTRO_SIZE_CONFIG[deviceKey] || INTRO_SIZE_CONFIG.DESKTOP;
    // For mobile/tablet: resolve orientation sub-key; for desktop: use directly
    const config = deviceConfig[orientationKey] || deviceConfig;

    // Cache result
    this._configCache = config;
    this._lastDevice = device;
    this._lastOrientation = orientation;

    return config;
  }

  /**
   * Apply proportional scaling and centering to spine animation
   * Scales based on symbol size and device type, positions relative to screen center
   */
  applyScaleAndCenter() {
    if (!this.introSpine || !this.dimensions) return;

    // Get orientation-specific config
    const config = this.getConfig();

    // Proportional scaling based on symbol size
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    let scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    // Apply configuration multiplier for overall size
    scale *= config.spineScaleMultiplier;

    this.introSpine.scale.set(scale);
    this.introSpine.x = 0;

    // Temporarily disabled — center spine at screen center
    // const { device, orientation } = this.dimensionsContext;
    // const deviceConfig = GAME_CONTAINER_OFFSET_CONFIG[device];
    // const reelOffset =
    //   typeof deviceConfig === "number"
    //     ? deviceConfig
    //     : (deviceConfig?.[orientation] ?? 0.2);
    // this.introSpine.y =
    //   -this.symbolHeight * reelOffset + (config.spineYOffset || 0);
    this.introSpine.y = config.spineYOffset || 0;

    // Update standalone button position
    this.updateButtonPosition();
  }

  /**
   * Update dimensions and symbol sizes, then recalculate scale and position
   * OPTIMIZED: Only recalculate when values actually change
   */
  updateDimensions(dimensionsContext, symbolHeight) {
    const oldDimensions = this.dimensionsContext?.dimensions;
    const newDimensions = dimensionsContext?.dimensions;
    const oldDevice = this.dimensionsContext?.device;
    const newDevice = dimensionsContext?.device;

    const dimensionsChanged =
      !oldDimensions ||
      oldDimensions.width !== newDimensions?.width ||
      oldDimensions.height !== newDimensions?.height ||
      this.dimensionsContext?.device !== dimensionsContext?.device ||
      this.dimensionsContext?.orientation !== dimensionsContext?.orientation;

    const symbolHeightChanged = this.symbolHeight !== symbolHeight;
    const deviceChanged = oldDevice !== newDevice;

    // Only proceed if something actually changed
    if (!dimensionsChanged && !symbolHeightChanged) {
      return;
    }

    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext.dimensions;
    this.symbolHeight = symbolHeight;

    // Invalidate config cache when device/orientation changes
    if (
      deviceChanged ||
      this._lastDevice !== dimensionsContext.device ||
      this._lastOrientation !== dimensionsContext.orientation
    ) {
      this._configCache = null; // Force getConfig() to recalculate
      this._lastDevice = dimensionsContext.device;
      this._lastOrientation = dimensionsContext.orientation;
    }

    // Update button text and style if device/orientation changed (tap vs click, font size)
    if (deviceChanged && this.buttonText) {
      this.updateButtonText();
    }
    this.updateButtonStyle();

    // Always update spine positioning whenever dimensions change
    if (
      this.introSpine &&
      this.dimensions &&
      (dimensionsChanged || symbolHeightChanged)
    ) {
      if (this.symbolHeight && this.symbolHeight > 0) {
        this.applyScaleAndCenter();
      }
    }
  }

  /**
   * Initialize the free spin intro spine
   */
  initializeIntroSpine() {
    try {
      this.introSpine = this.createIntroSpine();

      if (!this.introSpine) {
        return;
      }

      const missingComponents = [];
      if (!this.introSpine.skeleton) missingComponents.push("skeleton");
      if (!this.introSpine.state) missingComponents.push("state");
      if (!this.introSpine.skeleton?.data)
        missingComponents.push("skeleton.data");

      if (missingComponents.length > 0) {
        return;
      }

      this.container.addChild(this.introSpine);
      this.introSpine.alpha = 0.001;
      this.introSpine.zIndex = 20;

      this.applyScaleAndCenter();

      if (this.introSpine.parent !== this.container) {
        return;
      }

      this.initialized = true;

      // Invalidate animations cache when spine is reinitialized
      this._availableAnimationsCache = null;

      this.warmUpIntroSpine();

      this.initializeButton();
      this.initializeTexts();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Create intro spine from loaded assets
   */
  createIntroSpine() {
    try {
      const assets = PIXI.Assets.cache;
      const skeletonAlias = "dialogs-skeleton";
      const atlasAlias = "dialogs-atlas";

      if (!assets.has(skeletonAlias) || !assets.has(atlasAlias)) {
        return null;
      }

      const introSpine = Spine.from({
        skeleton: skeletonAlias,
        atlas: atlasAlias,
      });

      if (!introSpine?.skeleton?.data?.animations) {
        return null;
      }

      return introSpine;
    } catch (error) {
      return null;
    }
  }

  /**
   * Initialize skeleton by setting an animation to populate spine structure
   */
  warmUpIntroSpine() {
    if (!this.introSpine?.state) return;

    try {
      const animations = this.getAvailableAnimations();
      if (animations.length > 0) {
        this.introSpine.state.setAnimation(0, animations[0], false);
        this.introSpine.state.update(0.016);
        this.introSpine.skeleton.updateWorldTransform();
      }
      this.introSpine.alpha = 0.001;
    } catch {
      // Silent fail
    }
  }

  /**
   * Get available animations from the spine
   * Cached for performance - invalidated when spine is reinitialized
   */
  getAvailableAnimations() {
    if (!this.introSpine?.skeleton?.data?.animations) return [];

    // Check cache validity
    if (this._availableAnimationsCache !== null) {
      return this._availableAnimationsCache;
    }

    // Calculate and cache
    const animations = this.introSpine.skeleton.data.animations.map(
      (a) => a.name,
    );
    this._availableAnimationsCache = animations;

    return animations;
  }

  /**
   * Initialize tap-to-continue button and attach it to spine's "txt_slot_click_to" slot.
   * Uses addSlotObject so Spine auto-updates position/rotation/scale each frame.
   */
  initializeButton() {
    if (!this.introSpine || !this.introSpine.skeleton) {
      return;
    }

    // Create container as direct child of overlay (NOT attached to spine slot)
    this.buttonContainer = new PIXI.Container();
    this.buttonContainer.name = "free_spin_intro_continue_overlay";
    this.buttonContainer.alpha = 0.001;
    this.buttonContainer.zIndex = 1001;

    // Get translated button text based on device
    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    // Two-layer PRIMARY text (shadow + top)
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const fontSize = this._computeButtonFontSize();
    const scaled = this._computeScaledStrokeParams(fontSize);
    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
    };

    // Shadow layer — scale strokeThickness proportionally to font size
    this.buttonShadowText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.shadow,
        strokeThickness: scaled.shadowStroke,
      }),
    );
    this.buttonShadowText.resolution = 2;
    this.buttonShadowText.anchor.set(0.5);
    this.buttonShadowText.y = scaled.shadowOffsetY;

    // Top layer — scale strokeThickness proportionally to font size
    this.buttonText = new PIXI.Text(
      buttonText,
      new PIXI.TextStyle({
        ...sharedProps,
        ...primaryStyle.top,
        strokeThickness: scaled.topStroke,
      }),
    );
    this.buttonText.resolution = 2;
    this.buttonText.anchor.set(0.5);

    this.buttonContainer.addChild(this.buttonShadowText);
    this.buttonContainer.addChild(this.buttonText);

    // Add to overlay container directly (not to spine slot)
    this.container.addChild(this.buttonContainer);

    // Position based on device config
    this.updateButtonPosition();
  }

  /**
   * Update button position based on current dimensions and device config.
   * Button is positioned independently using symbolHeight * multiplier.
   * NOT affected by the reelset offset — only buttonYOffsetMultiplier controls it.
   * Called on init and on every resize.
   */
  updateButtonPosition() {
    if (!this.buttonContainer || !this.dimensions) return;

    const config = this.getConfig();
    const multiplier = config.buttonYOffsetMultiplier ?? 1.0;

    // Container is centered at screen center, so y=0 = center
    // Positive multiplier = push below center
    const buttonY = this.dimensions.height / 2 - this.symbolHeight * multiplier;

    this.buttonContainer.x = 0;
    this.buttonContainer.y = buttonY;
  }

  /**
   * Update button text style (font size) when device/orientation changes.
   * Recalculates fontSize proportionally to spine scale + buttonFontScale.
   */
  updateButtonStyle() {
    if (!this.buttonText || !this.buttonShadowText) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;
    const fontSize = this._computeButtonFontSize();
    const scaled = this._computeScaledStrokeParams(fontSize);

    // Update shadow text style — scale stroke proportionally
    this.buttonShadowText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.shadow,
      strokeThickness: scaled.shadowStroke,
    });
    this.buttonShadowText.y = scaled.shadowOffsetY;

    // Update top text style — scale stroke proportionally
    this.buttonText.style = new PIXI.TextStyle({
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "bold",
      ...primaryStyle.top,
      strokeThickness: scaled.topStroke,
    });
  }

  /**
   * Initialize all text slots for the free spin intro dialog.
   * Called once at init time — text containers are reused across show/hide cycles.
   */
  initializeTexts() {
    if (!this.introSpine?.skeleton) return;

    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    const secondaryStyle = PixiSettings.FONT_STYLES.SECONDARY;
    const slotText = PixiSettings.FONT_STYLES.SLOT_TEXT;

    // CONGRATULATIONS — PRIMARY two-layer, curved arc
    this.initializeCurvedCongratsText(primaryStyle, slotText);

    // YOU HAVE WON — PRIMARY two-layer
    this.initializePrimarySlotText(
      "txt_slot_youwon",
      this.t("you_won").toUpperCase(),
      primaryStyle,
      slotText,
      52,
    );

    // FREE SPINS — PRIMARY two-layer
    this.initializePrimarySlotText(
      "txt_slot_free_games",
      this.t("free_spins").toUpperCase(),
      primaryStyle,
      slotText,
      52,
    );

    // Free spin count (number) — SECONDARY flat, placeholder until updateFreeSpinCount is called
    this.freeSpinCountText = this.initializeSecondarySlotText(
      "txt_slot_fs_value",
      "",
      secondaryStyle,
      slotText,
      90,
    );
  }

  /**
   * Compute button font size proportionally to the spine scale.
   * Uses the same symbolHeight/nativeSize ratio the spine uses,
   * then applies the per-device buttonFontScale multiplier.
   */
  _computeButtonFontSize() {
    const baseFontSize = 32;
    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const spineRatio = Math.max(0.1, Math.min(targetSize / nativeSize, 2));
    const config = this.getConfig();
    return Math.round(
      baseFontSize *
        spineRatio *
        (config.spineScaleMultiplier || 1.0) *
        (config.buttonFontScale || 1.0),
    );
  }

  /**
   * Scale stroke thickness and shadow offset proportionally to the computed font size.
   * At the base font size (32px) the values match the raw PRIMARY style.
   * On smaller devices the ratio shrinks, keeping layers tight.
   */
  _computeScaledStrokeParams(fontSize) {
    const baseFontSize = 32;
    const ratio = Math.max(0.3, fontSize / baseFontSize);
    const primaryStyle = PixiSettings.FONT_STYLES.PRIMARY;
    return {
      shadowStroke: Math.max(1, Math.round((primaryStyle.shadow.strokeThickness || 4) * ratio)),
      topStroke: Math.max(1, Math.round((primaryStyle.top.strokeThickness || 3) * ratio)),
      shadowOffsetY: Math.max(1, Math.round((primaryStyle.shadowOffsetY || 3) * ratio)),
    };
  }

  /**
   * Create curved "CONGRATULATIONS" text along an arc and attach to spine slot.
   * Each letter is positioned individually along a circular arc with rotation.
   */
  initializeCurvedCongratsText(primaryStyle, slotText) {
    const slot = this.introSpine.skeleton.findSlot("txt_slot_congrats");
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = "txt_slot_congrats_curved_text";
    container.zIndex = 1000;

    const text = this.t("congratulations").toUpperCase();
    const fontSize = 58;
    const arcRadius = 2100; // Larger radius = less curvature
    const totalArcAngle = 0.3; // Total arc span in radians
    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "800",
    };

    const shadowStyle = new PIXI.TextStyle({
      ...sharedProps,
      ...primaryStyle.shadow,
    });
    const topStyle = new PIXI.TextStyle({
      ...sharedProps,
      ...primaryStyle.top,
    });

    // Measure each character width for proper spacing
    const charWidths = [];
    for (const char of text) {
      const metric = PIXI.TextMetrics.measureText(char, topStyle);
      charWidths.push(metric.width);
    }

    // Extra pixel gap between letters (prevents touching)
    const letterSpacingExtra = 14;

    // Convert pixel widths to angular spans using arc geometry,
    // then scale uniformly so the total fits the desired totalArcAngle.
    const rawAngularWidths = charWidths.map(
      (w) => (w + letterSpacingExtra) / arcRadius,
    );
    const rawTotalAngle = rawAngularWidths.reduce((sum, a) => sum + a, 0);
    const scaleFactor = totalArcAngle / rawTotalAngle;
    const angularWidths = rawAngularWidths.map((a) => a * scaleFactor);

    // Starting angle positions the first letter's center
    const startAngle = -Math.PI / 2 - totalArcAngle / 2; // Center the arc at top
    let currentAngle = startAngle + angularWidths[0] / 2;

    for (let i = 0; i < text.length; i++) {
      const angle = currentAngle;
      const x = Math.cos(angle) * arcRadius;
      const y = Math.sin(angle) * arcRadius + arcRadius; // Shift down so arc center is above
      const rotation = angle + Math.PI / 2; // Tangent to the arc

      // Shadow letter
      const shadowChar = new PIXI.Text(text[i], shadowStyle);
      shadowChar.resolution = 2;
      shadowChar.anchor.set(0.5);
      shadowChar.x = x;
      shadowChar.y = y + (primaryStyle.shadowOffsetY || 3);
      shadowChar.rotation = rotation;

      // Top letter
      const topChar = new PIXI.Text(text[i], topStyle);
      topChar.resolution = 2;
      topChar.anchor.set(0.5);
      topChar.x = x;
      topChar.y = y;
      topChar.rotation = rotation;

      container.addChild(shadowChar);
      container.addChild(topChar);

      // Advance by half current letter + half next letter (proper kerning)
      if (i < text.length - 1) {
        currentAngle += angularWidths[i] / 2 + angularWidths[i + 1] / 2;
      }
    }

    this.introSpine.addSlotObject("txt_slot_congrats", container);

    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
  }

  /**
   * Create a two-layer PRIMARY text and attach it to a spine slot.
   */
  initializePrimarySlotText(slotName, text, primaryStyle, slotText, fontSize) {
    const slot = this.introSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const sharedProps = {
      fontFamily: primaryStyle.fontFamily,
      ...slotText,
      fontSize,
      fontWeight: "800",
    };

    // Shadow layer
    const shadowText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.shadow }),
    );
    shadowText.resolution = 2;
    shadowText.anchor.set(0.5);
    shadowText.y = primaryStyle.shadowOffsetY || 3;

    // Top layer
    const topText = new PIXI.Text(
      text,
      new PIXI.TextStyle({ ...sharedProps, ...primaryStyle.top }),
    );
    topText.resolution = 2;
    topText.anchor.set(0.5);

    container.addChild(shadowText);
    container.addChild(topText);
    this.introSpine.addSlotObject(slotName, container);

    // Store refs for cleanup
    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);
  }

  /**
   * Create a single-layer SECONDARY text and attach it to a spine slot.
   */
  initializeSecondarySlotText(
    slotName,
    text,
    secondaryStyle,
    slotText,
    fontSize,
  ) {
    const slot = this.introSpine.skeleton.findSlot(slotName);
    if (!slot) return;

    const container = new PIXI.Container();
    container.name = `${slotName}_text_attached_to_slot`;
    container.zIndex = 1000;

    const textObj = new PIXI.Text(
      text,
      new PIXI.TextStyle({
        fontFamily: secondaryStyle.fontFamily,
        fill: secondaryStyle.fill,
        fillGradientType: secondaryStyle.fillGradientType,
        stroke: secondaryStyle.stroke,
        strokeThickness: secondaryStyle.strokeThickness,
        dropShadow: secondaryStyle.dropShadow,
        dropShadowColor: secondaryStyle.dropShadowColor,
        dropShadowDistance: secondaryStyle.dropShadowDistance,
        dropShadowBlur: secondaryStyle.dropShadowBlur,
        dropShadowAlpha: secondaryStyle.dropShadowAlpha,
        dropShadowAngle: secondaryStyle.dropShadowAngle,
        ...slotText,
        fontSize,
        fontWeight: "800",
      }),
    );
    textObj.resolution = 2;
    textObj.anchor.set(0.5);

    container.addChild(textObj);
    this.introSpine.addSlotObject(slotName, container);

    // Store ref for cleanup
    if (!this.textContainers) this.textContainers = [];
    this.textContainers.push(container);

    // Return the PIXI.Text for external updates
    return textObj;
  }

  /**
   * Update the free spin count text with the actual value from Redux.
   * Called by the component when showing the dialog.
   */
  updateFreeSpinCount(count) {
    if (this.freeSpinCountText) {
      this.freeSpinCountText.text = String(count);
    }
  }

  /**
   * Update button text based on current device (tap vs click)
   */
  updateButtonText() {
    if (!this.buttonText) return;

    const translationKey =
      this.dimensionsContext?.device === PixiSettings.DEVICE.DESKTOP
        ? "click_to_continue"
        : "tap_to_continue";
    const buttonText = this.t(translationKey).toUpperCase();

    this.buttonText.text = buttonText;
    if (this.buttonShadowText) {
      this.buttonShadowText.text = buttonText;
    }
  }

  /**
   * Show continue button with fade-in animation
   */
  showButton() {
    // Show PIXI button attached to bone
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 1,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    // Keep React button hidden (for backward compatibility)
    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Hide continue button with fade-out animation
   */
  hideButton() {
    if (this.buttonContainer) {
      anime({
        targets: this.buttonContainer,
        alpha: 0,
        duration: 300,
        easing: "easeOutQuad",
      });
    }

    if (this.buttonRef?.current) {
      this.buttonRef.current.alpha = 0;
    }
  }

  /**
   * Restart the sequence from scratch - used when tab becomes active again after being hidden.
   */
  restartSequence() {
    if (this.isWaitingForClick) {
      return;
    }

    if (!this.isPlaying) {
      return;
    }

    // Clear all pending timeouts from the interrupted sequence
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Stop bone update loop from interrupted sequence

    // Reset isPlaying so startSequence() guard doesn't block
    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.canSkipAnimation = false;
    this.isHandlingUserClick = false;

    // Restart cleanly
    this.startSequence();
  }

  /**
   * Start the intro animation sequence
   */
  startSequence() {
    return new Promise((resolve) => {
      if (this.isPlaying || !this.introSpine?.state) {
        resolve();
        return;
      }

      this.isPlaying = true;
      this.isWaitingForClick = false;
      this.overlayRef.current?.fadeInBackground();

      // Reset user interaction guards
      this.isHandlingUserClick = false;
      this.canSkipAnimation = false;

      // Clear any existing timeouts
      if (this.skipProtectionTimeout) {
        clearTimeout(this.skipProtectionTimeout);
        this.skipProtectionTimeout = null;
      }
      if (this.debounceTimeout) {
        clearTimeout(this.debounceTimeout);
        this.debounceTimeout = null;
      }

      // Play free_spin_intro sound when animation appears
      soundManager.play(SOUND_NAME.free_spin_intro);

      // Spine is already positioned correctly at all times (via updateDimensions)
      // Just ensure final positioning check before showing
      if (
        this.introSpine &&
        this.symbolHeight &&
        this.symbolHeight > 0 &&
        this.dimensions
      ) {
        this.applyScaleAndCenter();
      }

      this.introSpine.state.clearTracks();
      this.introSpine.state.clearListeners();
      this.introSpine.state.apply(this.introSpine.skeleton);
      this.introSpine.skeleton.setToSetupPose();

      // SKIP PROTECTION: Block skipping for the first second (matching H&W pattern)
      // Show button after 1 second
      this.skipProtectionTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.canSkipAnimation = true;
          this.showButton();
          this.isWaitingForClick = true;
        }
      }, 1000);

      // Play intro_in animation while STILL HIDDEN — lets GPU process new pose
      this.introSpine.state.setAnimation(0, "free_spin_intro_in", false);

      // Show on NEXT FRAME — old warmup pose is gone, new animation starts fresh
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (this.isPlaying && this.introSpine) {
          this.introSpine.alpha = 1;
        }
      });

      // Get intro_in animation duration
      const inDuration = this.getSpineAnimationDuration("free_spin_intro_in");

      // After intro_in completes, start looping intro_idle
      const inTimeout = setTimeout(() => {
        if (this.isPlaying) {
          this.introSpine.state.setAnimation(0, "free_spin_intro_idle", true);
          resolve(); // Sequence ready, waiting for user click
        }
      }, inDuration);

      // Store timeout for cleanup
      this.currentTimeout = inTimeout;
    });
  }

  /**
   * Get animation duration from spine data
   */
  getSpineAnimationDuration(animationName) {
    if (
      !this.introSpine ||
      !this.introSpine.skeleton ||
      !this.introSpine.skeleton.data ||
      !this.introSpine.skeleton.data.animations
    ) {
      return 1000; // Default fallback
    }

    try {
      const animation = this.introSpine.skeleton.data.animations.find(
        (anim) => anim.name === animationName,
      );

      if (animation && animation.duration) {
        const durationMs = Math.round(animation.duration * 1000);
        return durationMs;
      }

      return 1000; // Default fallback
    } catch (error) {
      return 1000; // Default fallback
    }
  }

  /**
   * Handle user click to close animation
   * Optimized debouncing - only 1 click processed
   */
  handleUserClick() {
    // Basic validation
    if (!this.isPlaying || !this.introSpine?.state) {
      return false;
    }

    // Block all clicks if debounce timeout exists
    if (this.debounceTimeout) {
      return false;
    }

    // Skip protection: block input for first second of animation
    if (!this.canSkipAnimation) {
      return false;
    }

    // Check if waiting for click
    if (!this.isWaitingForClick) {
      return false;
    }

    // Create debounce timeout to block subsequent clicks
    this.debounceTimeout = setTimeout(() => {
      this.debounceTimeout = null;
    }, 300);

    // Close the animation
    this.playClosingAnimation();

    return true;
  }

  /**
   * Play closing out animation when user clicks to continue
   */
  playClosingAnimation() {
    if (!this.isWaitingForClick || !this.introSpine?.state) return;

    this.isWaitingForClick = false;
    this.hideButton();

    // Stop intro sound
    soundManager.stop(SOUND_NAME.free_spin_intro);

    this.introSpine.state.clearListeners();

    this.introSpine.state.setAnimation(0, "free_spin_intro_out", false);
    this.introSpine.state.addListener({
      complete: (entry) => {
        if (entry.animation.name === "free_spin_intro_out" && this.isPlaying) {
          // Start transition immediately when spine out animation finishes
          if (this.dispatch) {
            this.dispatch(setFreeSpinTransitionActive(true));
          }
          this.finishAnimation();
        }
      },
    });
  }

  /**
   * Finish the animation and clean up
   */
  finishAnimation() {
    if (!this.introSpine || !this.initialized) return;

    this.isPlaying = false;
    this.isWaitingForClick = false;
    this.introSpine.alpha = 0.001;

    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Reset user interaction guards
    this.isHandlingUserClick = false;
    this.canSkipAnimation = false;

    // Wait for overlay fade out to complete before removing dialog
    this.overlayRef.current?.fadeOutBackground(() => {
      if (this.dispatch) {
        this.dispatch(setShowFreeSpinIntro(false));
      }
    });
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    // Cancel pending show RAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop sounds
    soundManager.stop(SOUND_NAME.free_spin_intro);

    // Clear all timeouts
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    if (this.skipProtectionTimeout) {
      clearTimeout(this.skipProtectionTimeout);
      this.skipProtectionTimeout = null;
    }
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout);
      this.debounceTimeout = null;
    }

    // Remove slot objects from spine
    if (this.introSpine?.removeSlotObjects) {
      this.introSpine.removeSlotObjects();
    }

    // Cleanup button
    if (this.buttonContainer) {
      this.buttonContainer.destroy({ children: true });
      this.buttonContainer = null;
      this.buttonShadowText = null;
      this.buttonText = null;
    }

    // Cleanup text containers
    if (this.textContainers) {
      for (const container of this.textContainers) {
        container.destroy({ children: true });
      }
      this.textContainers = null;
    }

    if (this.introSpine) {
      this.introSpine.state?.clearListeners();
      this.introSpine.state?.clearTracks();
      if (this.introSpine.state) this.introSpine.state.timeScale = 1.0;

      if (this.container?.children.includes(this.introSpine)) {
        this.container.removeChild(this.introSpine);
      }
      this.introSpine.destroy();
      this.introSpine = null;
    }

    this.initialized = false;
  }
}

export default FreeSpinIntroDialogController;
